import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Sparkles, Settings, Play, Clock, CheckCircle2, AlertTriangle,
  TrendingUp, TrendingDown, Target, Loader2, FileText, Mail,
  RefreshCw, ChevronRight, Calendar, ArrowRight, Shield,
  Zap, BarChart2, AlertCircle, ArrowUpRight
} from 'lucide-react';
import axios from 'axios';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const DAYS = [
  { value: 'sunday', label: 'Sunday' },
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
];

const TIMEZONES = [
  { value: 'UTC', label: 'UTC (GMT+0)' },
  { value: 'GMT+1', label: 'GMT+1 (CET/WAT)' },
  { value: 'GMT+2', label: 'GMT+2 (EET/CAT)' },
  { value: 'GMT+3', label: 'GMT+3 (EAT/AST)' },
  { value: 'GMT+4', label: 'GMT+4 (GST/MUT)' },
  { value: 'GMT+5', label: 'GMT+5 (PKT/UZT)' },
  { value: 'IST', label: 'GMT+5:30 (IST India)' },
  { value: 'GMT+6', label: 'GMT+6 (BST/BTT)' },
  { value: 'ICT', label: 'GMT+7 (ICT/WIB)' },
  { value: 'PHT', label: 'GMT+8 (PHT/SGT/HKT)' },
  { value: 'KST', label: 'GMT+9 (JST/KST)' },
  { value: 'AEST', label: 'GMT+10 (AEST)' },
  { value: 'NZST', label: 'GMT+12 (NZST)' },
  { value: 'GMT-3', label: 'GMT-3 (BRT/ART)' },
  { value: 'GMT-4', label: 'GMT-4 (AST/CLT)' },
  { value: 'EST', label: 'GMT-5 (EST/COT)' },
  { value: 'CST', label: 'GMT-6 (CST/MDT)' },
  { value: 'MST', label: 'GMT-7 (MST/PDT)' },
  { value: 'PST', label: 'GMT-8 (PST/AKDT)' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => {
  const h = String(i).padStart(2, '0');
  return { value: `${h}:00`, label: `${h}:00` };
});


// ─── Score Circle ───
function ScoreCircle({ score, label, size = 'lg' }) {
  const color = score >= 70 ? '#10B981' : score >= 40 ? '#F59E0B' : '#EF4444';
  const r = size === 'lg' ? 54 : 32;
  const stroke = size === 'lg' ? 6 : 4;
  const circ = 2 * Math.PI * (r - stroke);
  const offset = circ - (score / 100) * circ;
  const dim = r * 2;

  return (
    <div className="flex flex-col items-center" data-testid="insight-score">
      <svg width={dim} height={dim} viewBox={`0 0 ${dim} ${dim}`}>
        <circle cx={r} cy={r} r={r - stroke} fill="none" stroke="#1e293b" strokeWidth={stroke} />
        <circle cx={r} cy={r} r={r - stroke} fill="none" stroke={color} strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset}
          transform={`rotate(-90 ${r} ${r})`} className="transition-all duration-1000" />
        <text x={r} y={r - 4} textAnchor="middle" fill={color} fontSize={size === 'lg' ? 22 : 14} fontWeight="bold">{score}</text>
        <text x={r} y={r + (size === 'lg' ? 14 : 10)} textAnchor="middle" fill="#94a3b8" fontSize={size === 'lg' ? 10 : 8}>/100</text>
      </svg>
      {label && <p className="text-[10px] text-slate-200 mt-1">{label}</p>}
    </div>
  );
}


// ─── Insight Card ───
function InsightCard({ insight }) {
  const pColors = { high: 'bg-red-500/20 text-red-400', medium: 'bg-amber-500/20 text-amber-400', low: 'bg-emerald-500/20 text-emerald-400' };
  const catIcons = { Leads: Target, Marketing: TrendingUp, Service: Shield, Goals: CheckCircle2, Team: BarChart2 };
  const Icon = catIcons[insight.category] || Zap;

  return (
    <Card className="bg-slate-800/50 border-slate-700/50 hover:border-slate-600/50 transition-colors" data-testid={`insight-card-${insight.category}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-slate-700/50 flex items-center justify-center shrink-0 mt-0.5">
            <Icon className="h-4 w-4 text-slate-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge className={cn("text-[9px]", pColors[insight.priority] || pColors.medium)}>{insight.priority}</Badge>
              <span className="text-[10px] text-slate-500">{insight.category}</span>
            </div>
            <p className="text-sm font-semibold text-white mb-1">{insight.title}</p>
            <p className="text-xs text-slate-200 leading-relaxed mb-2">{insight.detail}</p>
            <div className="flex items-start gap-1.5 p-2 bg-emerald-500/10 rounded-md border border-emerald-500/20">
              <ArrowUpRight className="h-3 w-3 text-emerald-400 shrink-0 mt-0.5" />
              <p className="text-[11px] text-emerald-300">{insight.recommendation}</p>
            </div>
            {insight.impact && <p className="text-[10px] text-slate-300 mt-1.5 italic">Impact: {insight.impact}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


// ─── Report Detail View ───
function ReportDetail({ report }) {
  const ai = report?.ai_analysis || {};
  const snap = report?.data_snapshot || {};
  const metrics = ai.key_metrics || {};

  return (
    <div className="space-y-5" data-testid="report-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <ScoreCircle score={ai.health_score || 0} label={ai.health_label} />
        <div className="flex-1">
          <h2 className="text-lg font-bold text-white">{ai.headline || 'Business Report'}</h2>
          <p className="text-sm text-slate-200 mt-1 leading-relaxed">{ai.executive_summary}</p>
          <p className="text-[10px] text-slate-300 mt-1">Generated: {new Date(report.generated_at).toLocaleString()} | Period: {report.period}</p>
        </div>
      </div>

      {/* Highlights & Concerns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {ai.highlights?.length > 0 && (
          <Card className="bg-emerald-500/5 border-emerald-500/20">
            <CardContent className="p-3">
              <p className="text-xs font-semibold text-emerald-400 mb-2 flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" />Highlights</p>
              <ul className="space-y-1.5">
                {ai.highlights.map((h, i) => <li key={i} className="text-xs text-slate-300 flex items-start gap-2"><span className="text-emerald-400 shrink-0">+</span>{h}</li>)}
              </ul>
            </CardContent>
          </Card>
        )}
        {ai.concerns?.length > 0 && (
          <Card className="bg-red-500/5 border-red-500/20">
            <CardContent className="p-3">
              <p className="text-xs font-semibold text-red-400 mb-2 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" />Concerns</p>
              <ul className="space-y-1.5">
                {ai.concerns.map((c, i) => <li key={i} className="text-xs text-slate-300 flex items-start gap-2"><span className="text-red-400 shrink-0">!</span>{c}</li>)}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Key Metrics */}
      {Object.keys(metrics).length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {Object.entries(metrics).map(([key, val]) => (
            <div key={key} className="p-3 bg-slate-800/60 border border-slate-700/40 rounded-xl text-center">
              <p className="text-lg font-bold text-white">{val}</p>
              <p className="text-[9px] text-slate-300 capitalize">{key.replace(/_/g, ' ')}</p>
            </div>
          ))}
        </div>
      )}

      {/* Insights */}
      {ai.insights?.length > 0 && (
        <div className="space-y-2.5">
          <p className="text-xs font-semibold text-slate-300">Detailed Insights</p>
          <div className="grid gap-3">
            {ai.insights.map((ins, i) => <InsightCard key={i} insight={ins} />)}
          </div>
        </div>
      )}

      {/* Action Items */}
      {ai.action_items?.length > 0 && (
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-white mb-3 flex items-center gap-1.5"><Zap className="h-3.5 w-3.5 text-amber-400" />Action Items</p>
            <div className="space-y-2">
              {ai.action_items.map((a, i) => (
                <div key={i} className="flex items-start gap-3 p-2.5 bg-slate-900/50 rounded-lg" data-testid={`action-item-${i}`}>
                  <div className={cn("w-5 h-5 rounded flex items-center justify-center shrink-0 text-[9px] font-bold",
                    a.priority === 'high' ? "bg-red-500/20 text-red-400" : a.priority === 'medium' ? "bg-amber-500/20 text-amber-400" : "bg-emerald-500/20 text-emerald-400"
                  )}>{i + 1}</div>
                  <div>
                    <p className="text-xs text-white font-medium">{a.action}</p>
                    {a.expected_outcome && <p className="text-[10px] text-slate-300 mt-0.5">{a.expected_outcome}</p>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Forecast */}
      {ai.forecast && (
        <Card className="bg-blue-500/5 border-blue-500/20">
          <CardContent className="p-3">
            <p className="text-xs font-semibold text-blue-400 mb-1 flex items-center gap-1.5"><TrendingUp className="h-3.5 w-3.5" />Forecast</p>
            <p className="text-xs text-slate-300 italic">{ai.forecast}</p>
          </CardContent>
        </Card>
      )}

      {/* Raw Data Snapshot */}
      <Card className="bg-slate-800/30 border-slate-700/30">
        <CardContent className="p-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-300 font-medium mb-2">Data Snapshot</p>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {Object.entries(snap).filter(([,v]) => typeof v !== 'object').map(([k, v]) => (
              <div key={k} className="text-center">
                <p className="text-sm font-bold text-slate-300">{typeof v === 'number' ? v.toLocaleString() : v}</p>
                <p className="text-[8px] text-slate-300 capitalize">{k.replace(/_/g, ' ')}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════

export default function AIInsightsPage() {
  const [tab, setTab] = useState('latest');
  const [schedule, setSchedule] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [schedRes, histRes] = await Promise.all([
        axios.get(`${API}/api/ai-insights/schedule`),
        axios.get(`${API}/api/ai-insights/history`),
      ]);
      setSchedule(schedRes.data);
      setHistory(histRes.data.reports || []);
      if (histRes.data.reports?.length > 0) {
        setSelectedReport(histRes.data.reports[0]);
      }
    } catch { /* */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGenerate = async (period = 'weekly') => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/api/ai-insights/generate`, { period });
      toast.success('AI Insight generated!');
      setSelectedReport(res.data);
      setTab('latest');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate');
    }
    setGenerating(false);
  };

  const handleSaveSchedule = async (form) => {
    try {
      await axios.put(`${API}/api/ai-insights/schedule`, form);
      toast.success('Schedule saved');
      setSchedule(form);
      setSettingsOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-fadeIn" data-testid="ai-insights-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-emerald-500" />AI Business Insights<PageInfoTooltip pageKey="ai-business-insights" />
          </h1>
          <p className="text-sm text-gray-medium">AI-powered analysis of your business performance, trends, and recommendations</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => setSettingsOpen(true)} data-testid="insight-settings-btn">
            <Settings className="h-3.5 w-3.5" />Schedule
          </Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 gap-1.5 text-xs" onClick={() => handleGenerate('weekly')} disabled={generating} data-testid="generate-insight-btn">
            {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            Generate Now
          </Button>
        </div>
      </div>

      {/* Schedule Status */}
      {schedule && (
        <Card className={cn("border", schedule.enabled ? "bg-emerald-500/5 border-emerald-500/20" : "bg-slate-800/30 border-slate-700/30")}>
          <CardContent className="p-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", schedule.enabled ? "bg-emerald-500/20" : "bg-slate-700")}>
                <Clock className={cn("h-4 w-4", schedule.enabled ? "text-emerald-400" : "text-slate-500")} />
              </div>
              <div>
                <p className="text-xs font-medium text-white">
                  {schedule.enabled ? 'Auto-delivery active' : 'Auto-delivery disabled'}
                </p>
                <p className="text-[10px] text-slate-400">
                  {schedule.enabled
                    ? `Every ${schedule.day_of_week} at ${schedule.time} (${schedule.timezone}) to ${schedule.recipients?.length || 0} recipient(s)`
                    : 'Click "Schedule" to configure automatic delivery'}
                </p>
              </div>
            </div>
            {schedule.last_sent && (
              <Badge className="bg-slate-700 text-slate-300 text-[9px]">
                Last sent: {new Date(schedule.last_sent).toLocaleDateString()}
              </Badge>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-800/50">
          <TabsTrigger value="latest" className="text-xs gap-1.5" data-testid="tab-latest"><Sparkles className="h-3.5 w-3.5" />Latest Report</TabsTrigger>
          <TabsTrigger value="history" className="text-xs gap-1.5" data-testid="tab-history"><FileText className="h-3.5 w-3.5" />History</TabsTrigger>
        </TabsList>

        <TabsContent value="latest">
          {selectedReport ? (
            <ReportDetail report={selectedReport} />
          ) : (
            <Card className="bg-slate-800/30 border-slate-700/30">
              <CardContent className="p-12 text-center">
                <Sparkles className="h-10 w-10 text-slate-600 mx-auto mb-3" />
                <p className="text-sm text-slate-400">No insights generated yet</p>
                <p className="text-xs text-slate-300 mt-1">Click "Generate Now" to create your first AI business analysis</p>
                <Button className="mt-4 bg-emerald-600 hover:bg-emerald-500 text-xs gap-1.5" onClick={() => handleGenerate('weekly')} disabled={generating} data-testid="generate-first-btn">
                  {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  Generate First Report
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="history">
          <div className="space-y-2">
            {history.length === 0 && (
              <Card className="bg-slate-800/30 border-slate-700/30">
                <CardContent className="p-8 text-center">
                  <FileText className="h-8 w-8 text-slate-600 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">No report history</p>
                </CardContent>
              </Card>
            )}
            {history.map((r, i) => {
              const ai = r.ai_analysis || {};
              return (
                <Card key={r.insight_id || i}
                  className={cn("border cursor-pointer transition-colors",
                    selectedReport?.insight_id === r.insight_id ? "bg-emerald-500/10 border-emerald-500/30" : "bg-slate-800/30 border-slate-700/30 hover:border-slate-600"
                  )}
                  onClick={() => { setSelectedReport(r); setTab('latest'); }}
                  data-testid={`history-report-${i}`}>
                  <CardContent className="p-3 flex items-center gap-3">
                    <ScoreCircle score={ai.health_score || 0} size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{ai.headline || 'Business Report'}</p>
                      <p className="text-[10px] text-slate-400">{new Date(r.generated_at).toLocaleString()} | {r.period}</p>
                    </div>
                    <Badge className={cn("text-[9px] shrink-0",
                      (ai.health_score || 0) >= 70 ? "bg-emerald-500/20 text-emerald-400" :
                      (ai.health_score || 0) >= 40 ? "bg-amber-500/20 text-amber-400" : "bg-red-500/20 text-red-400"
                    )}>{ai.health_label || 'N/A'}</Badge>
                    <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Schedule Settings Dialog */}
      {settingsOpen && <ScheduleDialog schedule={schedule} onSave={handleSaveSchedule} onClose={() => setSettingsOpen(false)} />}
    </div>
  );
}


// ─── Schedule Settings Dialog ───
function ScheduleDialog({ schedule, onSave, onClose }) {
  const [form, setForm] = useState({
    enabled: schedule?.enabled ?? false,
    day_of_week: schedule?.day_of_week || 'monday',
    time: schedule?.time || '09:00',
    timezone: schedule?.timezone || 'UTC',
    frequency: schedule?.frequency || 'weekly',
    recipients: schedule?.recipients || [''],
    sections: schedule?.sections || ['leads', 'marketing', 'service', 'goals', 'content', 'meetings'],
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (form.enabled && form.recipients.filter(r => r.trim()).length === 0) {
      toast.error('Add at least one recipient email'); return;
    }
    setSaving(true);
    await onSave({ ...form, recipients: form.recipients.filter(r => r.trim()) });
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="schedule-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Calendar className="h-4 w-4 text-emerald-500" />AI Insight Schedule</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Enable Toggle */}
          <div className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg border border-slate-700/30">
            <div>
              <p className="text-sm font-medium">Auto-delivery</p>
              <p className="text-[10px] text-slate-400">Automatically generate and email AI insights on schedule</p>
            </div>
            <Switch checked={form.enabled} onCheckedChange={v => setForm(f => ({ ...f, enabled: v }))} data-testid="schedule-toggle" />
          </div>

          {/* Day & Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Day of Week</Label>
              <Select value={form.day_of_week} onValueChange={v => setForm(f => ({ ...f, day_of_week: v }))}>
                <SelectTrigger className="mt-1 h-8 text-xs" data-testid="schedule-day"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DAYS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Time</Label>
              <Select value={form.time} onValueChange={v => setForm(f => ({ ...f, time: v }))}>
                <SelectTrigger className="mt-1 h-8 text-xs" data-testid="schedule-time"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {HOURS.map(h => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Timezone */}
          <div>
            <Label className="text-xs">Timezone</Label>
            <Select value={form.timezone} onValueChange={v => setForm(f => ({ ...f, timezone: v }))}>
              <SelectTrigger className="mt-1 h-8 text-xs" data-testid="schedule-timezone"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIMEZONES.map(tz => <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Frequency */}
          <div>
            <Label className="text-xs">Frequency</Label>
            <Select value={form.frequency} onValueChange={v => setForm(f => ({ ...f, frequency: v }))}>
              <SelectTrigger className="mt-1 h-8 text-xs" data-testid="schedule-frequency"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="quarterly">Quarterly</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Recipients */}
          <div>
            <Label className="text-xs">Recipients</Label>
            <div className="space-y-1.5 mt-1">
              {form.recipients.map((email, i) => (
                <div key={i} className="flex gap-1.5">
                  <Input placeholder="email@company.com" type="email" value={email}
                    onChange={e => setForm(f => ({ ...f, recipients: f.recipients.map((r, j) => j === i ? e.target.value : r) }))}
                    className="h-8 text-xs" data-testid={`recipient-${i}`} />
                  {form.recipients.length > 1 && (
                    <Button variant="ghost" size="sm" className="h-8 px-2 text-slate-500"
                      onClick={() => setForm(f => ({ ...f, recipients: f.recipients.filter((_, j) => j !== i) }))}>x</Button>
                  )}
                </div>
              ))}
              <Button variant="ghost" size="sm" className="text-xs text-emerald-400 h-7 gap-1"
                onClick={() => setForm(f => ({ ...f, recipients: [...f.recipients, ''] }))} data-testid="add-recipient-btn">
                <Mail className="h-3 w-3" />Add Recipient
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500" data-testid="save-schedule-btn">
            {saving ? 'Saving...' : 'Save Schedule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
